/* eslint-disable no-unused-vars */
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import type {ExpertHousing, FrontendError} from '~/types/oring-api-responses'
import type * as OringApiRequests from '@/types/oring-api'
import {useExpertHousingConverter} from './useExpertHousingConverter'
import useI18nExtended from '~/composables/usei18nExtended'

export function useHousingValidator() {
  const store = useORingCalculatorStore()
  const {oringDesignCockpitCurrentHousing, oringCalculatorImperial} = storeToRefs(store)
  const i18n = useI18nExtended()

  // Local ref to store validation errors by field key
  const validationErrors = ref<Record<string, string>>({})

  // Convert toleranceData and parameterInputData to ExpertHousing object
  function buildExpertHousingFromData(
    toleranceData: Record<string, string | null>,
    parameterInputData: Record<string, number | null>
  ): ExpertHousing | null {
    const converter = useExpertHousingConverter()

    const inputData = {
      toleranceData,
      parameterInputData
    }

    return converter.buildExpertHousingFromData(
      oringDesignCockpitCurrentHousing.value,
      inputData,
      store.toleranceClasses,
      store.oringDesignCockpitToleranceParameters()
    )
  }

  // Call the checkHousingExpertMode API with the built housing object
  async function callCheckHousingAPI(
    housing: ExpertHousing,
    emitValidationErrors: (errors: FrontendError[]) => void,
    emitHousingValidated: (housing: ExpertHousing) => void,
    emitApiError: (error: any) => void
  ): Promise<void> {
    try {
      const oringService = useOringCalculatorService()

      const request: OringApiRequests.ORingExpertModeCheckHousingRequest = {
        language: useNuxtApp().$i18n.locale.value,
        imperial: store.oringCalculatorImperial,
        housing: housing
      }

      const result = await oringService.checkHousingExpertMode(request)

      // Handle the result - you might want to emit an event or update store
      // If there are errors, you might want to display them to the user
      if (result.errorDTO?.error) {
        // Handle housing validation errors and map them to fields
        mapValidationErrorsToFields(result.errorDTO.errors)
        emitValidationErrors(result.errorDTO.errors)
      } else {
        // Clear any existing errors when validation passes
        validationErrors.value = {}
        emitHousingValidated(housing)
      }

    } catch (error) {
      // Handle API error
      emitApiError(error)
    }
  }

  // Store the latest housing and emit functions for re-validation on unit change
  let lastHousing: ExpertHousing | null = null
  let lastEmitValidationErrors: ((errors: FrontendError[]) => void) | null = null
  let lastEmitHousingValidated: ((housing: ExpertHousing) => void) | null = null
  let lastEmitApiError: ((error: any) => void) | null = null

  // Check housing with built housing data
  function checkHousing(
    toleranceData: Record<string, string | null>,
    parameterInputData: Record<string, number | null>,
    emitValidationErrors: (errors: FrontendError[]) => void,
    emitHousingValidated: (housing: ExpertHousing) => void,
    emitApiError: (error: any) => void
  ) {
    const housing = buildExpertHousingFromData(toleranceData, parameterInputData)

    if (housing) {
      // Store the latest arguments for re-validation on unit change
      lastHousing = housing
      lastEmitValidationErrors = emitValidationErrors
      lastEmitHousingValidated = emitHousingValidated
      lastEmitApiError = emitApiError
      callCheckHousingAPI(housing, emitValidationErrors, emitHousingValidated, emitApiError)
    }
  }

  // Extract field key from validation error
  function extractFieldKeyFromError(error: FrontendError): string | null {
    // Try to extract field key from elementName first
    if (error.elementName) {
      const elementName = error.elementName.toLowerCase()

      // Direct mapping from element names to field keys
      const elementToFieldMap: Record<string, string> = {
        'd1': 'd1',
        'd2': 'd2',
        'r1': 'r1',
        'd3': 'd3',
        'd4': 'd4',
        'd5': 'd5',
        'd6': 'd6',
        'd8': 'd8',
        'd9': 'd9',
        'd10': 'd10',
        'b1': 'b1',
        'b1in': 'b1in',
        'b1out': 'b1out',
        'b4': 'b4',
        'gap': 'gap',
        'gapwidth': 'gap',
        'height': 'height',
        'h': 'height'
      }

      // Check for exact match first
      if (elementToFieldMap[elementName]) {
        return elementToFieldMap[elementName]
      }

      // Check for partial matches in element name
      for (const [element, fieldKey] of Object.entries(elementToFieldMap)) {
        if (elementName.includes(element)) {
          return fieldKey
        }
      }
    }

    // Try to extract from error message if elementName doesn't work
    if (error.errorMessage) {
      const message = error.errorMessage.toLowerCase()

      // Look for parameter names in error message
      if (message.includes('d1')) {
        return 'd1'
      }
      if (message.includes('d2')) {
        return 'd2'
      }
      if (message.includes('r1')) {
        return 'r1'
      }
      if (message.includes('d3')) {
        return 'd3'
      }
      if (message.includes('d4')) {
        return 'd4'
      }
      if (message.includes('d5')) {
        return 'd5'
      }
      if (message.includes('d6')) {
        return 'd6'
      }
      if (message.includes('d8')) {
        return 'd8'
      }
      if (message.includes('d9')) {
        return 'd9'
      }
      if (message.includes('d10')) {
        return 'd10'
      }
      if (message.includes('b1out')) {
        return 'b1out'
      }
      if (message.includes('b1in')) {
        return 'b1in'
      }
      if (message.includes('b1')) {
        return 'b1'
      }
      if (message.includes('b4')) {
        return 'b4'
      }
      if (message.includes('gap')) {
        return 'gap'
      }
      if (message.includes('height')) {
        return 'height'
      }
    }

    return null
  }

  // Map validation errors to field keys
  function mapValidationErrorsToFields(errors: FrontendError[]) {
    // Clear existing errors
    validationErrors.value = {}

    // Map errors to field keys based on error codes or field identifiers
    errors.forEach(error => {
      // Extract field key from error code or use a mapping
      const fieldKey = extractFieldKeyFromError(error)
      if (fieldKey) {
        let errorMessage = error.errorMessage || error.elementName || 'Validation error'

        // Append threshold value with units if available
        if (error.thresholdValue !== null && error.thresholdValue !== undefined) {
          const isImperial = oringCalculatorImperial.value
          const unit = isImperial
            ? i18n.tv('inch', 'unitsOfMeasure')
            : i18n.tv('mm', 'unitsOfMeasure')
          // Round threshold value appropriately
          const decimals = isImperial ? 4 : 3
          const roundedValue = Number(error.thresholdValue).toFixed(decimals)
          errorMessage += ` ${roundedValue} ${unit}`
        }

        validationErrors.value[fieldKey] = errorMessage
      }
    })
  }

  // Get error message for a specific field
  function getErrorForField(key: string): string {
    return validationErrors.value[key] || ''
  }

  // Validate function
  function validate(): boolean {
    return Object.keys(validationErrors.value).length === 0
  }

  return {
    validationErrors: readonly(validationErrors),
    buildExpertHousingFromData,
    callCheckHousingAPI,
    checkHousing,
    extractFieldKeyFromError,
    mapValidationErrorsToFields,
    getErrorForField,
    validate
  }
}
